import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Tag, Plus, Trash2, Printer, Eye, X, Download, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PHARMACY_INFO = {
  name: 'صيدلية مار يوحنا زغرتا',
  phone: '06/663197 - 81/450539',
};

const CATEGORY_LABELS = {
  doseForm: { english: 'Dose / Form', arabic: 'الجرعة / الشكل' },
  frequency: { english: 'Frequency', arabic: 'التكرار' },
  foodRelation: { english: 'Relation to Food', arabic: 'العلاقة بالأكل' },
  duration: { english: 'Duration', arabic: 'المدة' },
};

const PrescriptionLabelDialog = ({ open, onOpenChange }) => {
  const { currentOrg } = useAuth();
  const [options, setOptions] = useState({
    doseForm: [],
    frequency: [],
    foodRelation: [],
    duration: [],
  });
  const [loading, setLoading] = useState(true);
  const [selections, setSelections] = useState({
    doseForm: '',
    frequency: '',
    foodRelation: '',
    duration: '',
  });
  const [preview, setPreview] = useState('');
  const [canPrint, setCanPrint] = useState(false);
  
  // Add new option states
  const [showAddForm, setShowAddForm] = useState({
    doseForm: false,
    frequency: false,
    foodRelation: false,
    duration: false,
  });
  const [newOption, setNewOption] = useState({
    english: '',
    arabic: '',
  });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  // Fetch options from API
  const fetchOptions = useCallback(async () => {
    if (!currentOrg?.id) return;
    
    setLoading(true);
    try {
      const res = await axios.get(`${API}/prescription-label-options?organization_id=${currentOrg.id}`);
      setOptions(res.data);
    } catch (err) {
      console.error('Failed to fetch label options:', err);
    } finally {
      setLoading(false);
    }
  }, [currentOrg?.id]);

  useEffect(() => {
    if (open && currentOrg?.id) {
      fetchOptions();
    }
  }, [open, currentOrg?.id, fetchOptions]);

  // Generate preview when selections change
  useEffect(() => {
    const parts = [];
    const categories = ['doseForm', 'frequency', 'foodRelation', 'duration'];
    
    categories.forEach(category => {
      const selectedId = selections[category];
      if (selectedId) {
        const option = options[category]?.find(o => o.id === selectedId);
        if (option) {
          parts.push(option.arabic);
        }
      }
    });
    
    const instruction = parts.join(' ');
    setPreview(instruction);
    setCanPrint(parts.length > 0);
  }, [selections, options]);

  const handleSelectionChange = (category, value) => {
    setSelections(prev => ({ ...prev, [category]: value }));
  };

  const toggleAddForm = (category) => {
    setShowAddForm(prev => ({ ...prev, [category]: !prev[category] }));
    setNewOption({ english: '', arabic: '' });
  };

  const handleAddOption = async (category) => {
    if (!newOption.english.trim() || !newOption.arabic.trim()) {
      alert('Please fill both English and Arabic fields');
      return;
    }
    
    const key = newOption.english.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    
    setSaving(true);
    try {
      await axios.post(`${API}/prescription-label-options`, {
        organization_id: currentOrg.id,
        category,
        key,
        english: newOption.english.trim(),
        arabic: newOption.arabic.trim(),
      });
      
      await fetchOptions();
      setShowAddForm(prev => ({ ...prev, [category]: false }));
      setNewOption({ english: '', arabic: '' });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add option');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOption = async (category, optionId) => {
    const option = options[category]?.find(o => o.id === optionId);
    if (!option) return;
    
    if (!window.confirm(`Delete "${option.english}"?`)) return;
    
    try {
      await axios.delete(`${API}/prescription-label-options/${optionId}`);
      await fetchOptions();
      
      // Reset selection if deleted
      if (selections[category] === optionId) {
        setSelections(prev => ({ ...prev, [category]: '' }));
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete option');
    }
  };

  const handleImportDefaults = async () => {
    if (!window.confirm('Import default prescription label options? This will add any missing default options.')) return;
    
    setImporting(true);
    try {
      const res = await axios.post(`${API}/prescription-label-options/import-defaults?organization_id=${currentOrg.id}`);
      alert(res.data.message);
      await fetchOptions();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to import defaults');
    } finally {
      setImporting(false);
    }
  };

  const handlePrint = () => {
    if (!preview) return;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Prescription Label</title>
        <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap" rel="stylesheet">
        <style>
          @page {
            size: 70mm 30mm;
            margin: 0;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          html, body {
            width: 70mm;
            height: 30mm;
            font-family: 'Cairo', sans-serif;
          }
          .label {
            width: 70mm;
            height: 30mm;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
            padding: 2mm;
          }
          .pharmacy-name {
            font-size: 8pt;
            font-weight: bold;
            line-height: 1.2;
          }
          .pharmacy-phone {
            font-size: 7pt;
            margin-bottom: 2mm;
          }
          .instruction {
            font-size: 11pt;
            font-weight: bold;
            line-height: 1.3;
            direction: rtl;
          }
        </style>
      </head>
      <body>
        <div class="label">
          <div class="pharmacy-name" dir="rtl">${PHARMACY_INFO.name}</div>
          <div class="pharmacy-phone">${PHARMACY_INFO.phone}</div>
          <div class="instruction">${preview}</div>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=300,height=200');
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const resetSelections = () => {
    setSelections({
      doseForm: '',
      frequency: '',
      foodRelation: '',
      duration: '',
    });
  };

  const hasNoOptions = !loading && Object.values(options).every(arr => arr.length === 0);

  const renderCategory = (category) => {
    const labels = CATEGORY_LABELS[category];
    const categoryOptions = options[category] || [];
    
    return (
      <div className="space-y-3">
        <Label className="text-base font-medium flex items-center justify-between">
          <span>{labels.english}</span>
          <span className="text-muted-foreground font-arabic text-base" dir="rtl">{labels.arabic}</span>
        </Label>
        
        <Select
          value={selections[category]}
          onValueChange={(value) => handleSelectionChange(category, value)}
        >
          <SelectTrigger className="text-base h-12 font-arabic" dir="rtl">
            <SelectValue placeholder={`اختر ${labels.arabic}`} />
          </SelectTrigger>
          <SelectContent>
            {categoryOptions.map((opt) => (
              <SelectItem key={opt.id} value={opt.id} className="font-arabic text-base py-3" dir="rtl">
                {opt.arabic}
              </SelectItem>
            ))}
            {categoryOptions.length === 0 && (
              <div className="p-3 text-sm text-muted-foreground text-center">
                No options available. Click "Add" or import defaults.
              </div>
            )}
          </SelectContent>
        </Select>
        
        {/* Add/Delete buttons */}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-sm flex-1"
            onClick={() => toggleAddForm(category)}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
          {selections[category] && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-sm text-red-500 hover:text-red-600"
              onClick={() => handleDeleteOption(category, selections[category])}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        {/* Add form */}
        {showAddForm[category] && (
          <div className="p-3 bg-muted/50 rounded space-y-3">
            <Input
              placeholder="English"
              className="h-10 text-sm"
              value={newOption.english}
              onChange={(e) => setNewOption(prev => ({ ...prev, english: e.target.value }))}
            />
            <Input
              placeholder="العربية"
              className="h-10 text-base font-arabic"
              dir="rtl"
              value={newOption.arabic}
              onChange={(e) => setNewOption(prev => ({ ...prev, arabic: e.target.value }))}
            />
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                className="h-9 text-sm flex-1"
                onClick={() => handleAddOption(category)}
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 text-sm"
                onClick={() => toggleAddForm(category)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Tag className="w-6 h-6" />
            Prescription Label
            <span className="font-arabic text-muted-foreground text-lg" dir="rtl">ملصق الوصفة</span>
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 py-6">
            {/* Left: Selections */}
            <div className="space-y-6">
              {hasNoOptions && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                  <p className="text-sm text-amber-800 mb-3">
                    No label options found. Import the default options to get started.
                  </p>
                  <Button
                    size="sm"
                    onClick={handleImportDefaults}
                    disabled={importing}
                    className="w-full"
                  >
                    {importing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Import Default Options
                  </Button>
                </div>
              )}
              
              {renderCategory('doseForm')}
              {renderCategory('frequency')}
              {renderCategory('foodRelation')}
              {renderCategory('duration')}
            </div>
            
            {/* Right: Preview */}
            <div className="space-y-6">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Label Preview
              </Label>
              
              {/* Label preview box - simulates 70mm x 30mm aspect ratio */}
              <div 
                className="border-2 border-dashed border-primary/30 rounded-lg bg-white p-6 flex flex-col items-center justify-center text-center"
                style={{ aspectRatio: '70/30', minHeight: '160px' }}
              >
                <div className="font-arabic font-bold text-lg" dir="rtl">
                  {PHARMACY_INFO.name}
                </div>
                <div className="text-sm text-muted-foreground mb-3">
                  {PHARMACY_INFO.phone}
                </div>
                <div className="font-arabic font-bold text-2xl leading-tight" dir="rtl">
                  {preview || <span className="text-muted-foreground text-base">Select options to preview</span>}
                </div>
              </div>
              
              <div className="text-sm text-muted-foreground text-center">
                Label size: 70mm × 30mm
              </div>
              
              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 h-12 text-base"
                  onClick={resetSelections}
                >
                  Reset
                </Button>
                <Button
                  className="flex-1 h-12 text-base"
                  onClick={handlePrint}
                  disabled={!canPrint}
                >
                  <Printer className="w-5 h-5 mr-2" />
                  Print Label
                </Button>
              </div>
              
              {/* Import defaults button (if has some options but not all) */}
              {!hasNoOptions && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={handleImportDefaults}
                  disabled={importing}
                >
                  {importing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Import Missing Defaults
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PrescriptionLabelDialog;
